import { router } from 'expo-router';
import { Pressable, StyleSheet, View } from 'react-native';

import { IconChevronRight, InlineAlert, Text, XStack, YStack, tokens } from '@fhs/ui';
import { formatCentsToDollarString } from '@fhs/utils';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import {
  CHECKOUT_PAYMENT_DETAILS_HEADING,
  CHECKOUT_PAYMENT_METHOD_BUTTON,
} from '@fhs-legacy/frontend/src/utils/test-ids';

import { useGiftCardCheckoutValidation } from '../../../hooks/use-gift-card-checkout-validation';
import { useSelectedGiftCard } from '../../../hooks/use-selected-gift-card';
import { usePaymentMethodStore } from '../../../payment';
import { NewCardForm } from '../../../payment/components';
import { logPaymentMethodSelectionOpened } from '../../../utils/logging-utils';

import { getGiftCardDisplay, getPaymentMethodDisplay } from './payment-method-icon-map';

export default function CheckoutPaymentDetails() {
  const { giftCardMissingAmount } = useGiftCardCheckoutValidation();
  const selectedPaymentMethod = usePaymentMethodStore(state => state.selectedPaymentMethod);
  const showAddPaymentMethod = usePaymentMethodStore(state => state.showAddPaymentMethod);
  const savedCardData = usePaymentMethodStore(state => state.savedCardData);
  const selectedGiftCard = useSelectedGiftCard();
  const { logRBIEvent } = useCRMEventsContext();

  const paymentDisplay =
    selectedPaymentMethod === 'giftCard'
      ? getGiftCardDisplay(selectedGiftCard?.last4 ?? '')
      : getPaymentMethodDisplay(selectedPaymentMethod, savedCardData);

  const hasError = Boolean(giftCardMissingAmount);
  const giftCardBalanceInsufficientMessage =
    hasError && giftCardMissingAmount
      ? `Your gift card balance is insufficient. Please add at least $${giftCardMissingAmount} to complete your order.`
      : '';

  const handleOpenPaymentSelection = () => {
    logPaymentMethodSelectionOpened(logRBIEvent);
    router.setParams({ action: 'payment' });
  };

  return (
    <>
      <Text.Heading type="four" style={styles.heading} testID={CHECKOUT_PAYMENT_DETAILS_HEADING}>
        Payment Details
      </Text.Heading>

      <Pressable
        testID={CHECKOUT_PAYMENT_METHOD_BUTTON}
        style={styles.anchor}
        focusable
        onPress={handleOpenPaymentSelection}
        onAccessibilityTap={handleOpenPaymentSelection}
        accessibilityRole="button"
        accessibilityLabel={`Payment method: ${paymentDisplay.label}`}
        accessibilityHint="Tap to change payment method"
      >
        {props => (
          <YStack
            style={[
              styles.container,
              props.pressed && styles.pressedContainer,
              hasError && styles.errorContainer,
            ]}
          >
            <XStack style={styles.cardContainer}>
              <View
                style={[
                  styles.iconCircle,
                  props.pressed && styles.iconCirclePressed,
                  hasError && styles.iconCircleError,
                ]}
              >
                {paymentDisplay.icon}
              </View>
              <YStack style={styles.textContainer}>
                <Text.Ui
                  weight="semibold"
                  size="lg"
                  style={[styles.text, hasError && styles.textError]}
                >
                  {paymentDisplay.label}
                </Text.Ui>
                {selectedPaymentMethod === 'giftCard' && (
                  <Text.Ui size="md" style={[styles.text, hasError && styles.textError]}>
                    Balance: ${formatCentsToDollarString(selectedGiftCard?.currentBalance ?? 0)}
                  </Text.Ui>
                )}
              </YStack>
              <IconChevronRight
                size={24}
                color={hasError ? tokens.colors.$error : tokens.colors.$houseDark}
              />
            </XStack>
            {giftCardBalanceInsufficientMessage && (
              <InlineAlert type="error" message={giftCardBalanceInsufficientMessage} />
            )}
          </YStack>
        )}
      </Pressable>

      {/* Show the form if the user chose to add a card or if there isn't a default or selected payment method  */}
      {(showAddPaymentMethod || !selectedPaymentMethod) && <NewCardForm />}
    </>
  );
}

const styles = StyleSheet.create({
  heading: {
    marginTop: 20,
    marginBottom: 12,
    marginHorizontal: 28,
  },
  pressedContainer: {
    backgroundColor: tokens.colors.$houseLight,
  },
  container: {
    width: '100%',
    borderColor: tokens.colors.$black10,
    backgroundColor: tokens.colors.$white,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    gap: 8,
    padding: 16,
  },
  cardContainer: {
    gap: 8,
    alignItems: 'center',
  },
  errorContainer: {
    borderColor: tokens.colors.$error,
    borderWidth: 1,
  },
  textContainer: {
    flex: 1,
  },
  text: {
    flex: 1,
  },
  anchor: {
    marginHorizontal: 28,
  },
  iconCirclePressed: {
    backgroundColor: tokens.colors.$black10,
  },
  iconCircle: {
    height: 40,
    width: 40,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.$houseLight,
  },
  iconCircleError: {
    backgroundColor: tokens.colors.$errorBackground,
  },
  textError: {
    color: tokens.colors.$error,
  },
});
